import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

import Loader2 from "../../elements/Loader2";
import commoncss from "../../css/common.module.css";
import createRegCss from "../../css/createregister.module.css";
import buttoncss from "../../css/button.module.css";
import InputGroup from "../../elements/InputGroup";
import Breadcrumb from "../../components/Breadcrumb";
import { useParams } from "react-router-dom";
import accountant from "../../../../src/assets/signIn.svg";
import ApproverLeaseModal from "../leases/ApproverLeaseModal";
import { useHistory } from "react-router";
import { ReactComponent as DownArrow } from "../../assets/icons/down-arrow.svg";
import {
  editInvestmentRegisterAPI,
  getInvestmentRegisterForms,
  GetAllCompanyData,
  getInvestmentSummary,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import swal from "sweetalert";

const getCompanyOption = (company) => ({
  label: company?.company_name || company?.name || company?.label || "",
  value: company?._id || company?.companyID || company?.value || "",
});

const normalizeSelectedCompanies = (record = {}, companyOptions = []) => {
  if (Array.isArray(record?.linkedCompaniesData) && record.linkedCompaniesData.length) {
    return record.linkedCompaniesData
      .map(getCompanyOption)
      .filter((company) => company.value && company.label);
  }

  if (Array.isArray(record?.linkedCompanies) && record.linkedCompanies.length) {
    return record.linkedCompanies
      .map((companyId) => {
        const id = companyId?._id || companyId?.companyID || companyId?.value || companyId;
        const name = companyId?.company_name || companyId?.name || companyId?.label;
        const matchedCompany = companyOptions.find((opt) => opt.value === id || opt.value === String(id));
        if (matchedCompany) return matchedCompany;
        if (name) return { label: name, value: id };
        return { label: String(id), value: id };
      })
      .filter((company) => company.value);
  }

  return [];
};

const EditInvestmentRegister = ({
  companyOptions = [],
  registerData = {},
  onSave,
  onSendForApproval,
}) => {
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("user"));
  const { registerid, id } = useParams();
  const investmentID = registerid || id;

  const [loader, setLoader] = useState(false);
  const [regName, setRegName] = useState(registerData?.name || "");
  const [selectedCompanies, setSelectedCompanies] = useState(
    registerData?.linkedCompanies || []
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [approverId, setApproverId] = useState(null);
  const [approverLeaseModal, setApproverLeaseModal] = useState(false);
  const [isApprovalPending, setIsApprovalPending] = useState(
    registerData?.approvalStatus?.includes("pending") || false
  );
  const [allCompanyOptions, setAllCompanyOptions] = useState(companyOptions || []);
  const [companiesWithInvestments, setCompaniesWithInvestments] = useState([]);
  const [companySearch, setCompanySearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const dropdownRef = useRef(null);
  const fetchedInvestmentRef = useRef(null);

  // Sync state if registerData prop changes
  useEffect(() => {
    if (registerData?.name) setRegName(registerData.name);
    if (registerData?.linkedCompanies)
      setSelectedCompanies(registerData.linkedCompanies);
    if (registerData?.approvalStatus)
      setIsApprovalPending(registerData.approvalStatus.includes("pending"));
  }, [registerData]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setCompanySearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSave = async () => {
    if (!investmentID) {
      console.error("[EditInvestmentRegister] Missing investmentID on save");
      setWarningPopup({
        isOpen: true,
        title: "Error",
        message: "Invalid register ID. Please refresh and try again.",
      });
      return;
    }

    const payload = {
      name: regName?.trim(),
      linkedCompanies: selectedCompanies.map((c) => c.value).filter(Boolean),
    };

    try {
      console.log("[EditInvestmentRegister] PATCH payload:", payload);
      const res = await editInvestmentRegisterAPI(investmentID, payload);
      if (res.status === 200) {
        swal("Success", "Investment Register updated successfully", "success");
        history.push("/dashboard"); 
      }
    } catch (error) {
      console.error("[EditInvestmentRegister] Update failed:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "There are existing investments in this company, you cannot remove it";
      setWarningPopup({
        isOpen: true,
        title: "You cannot remove the company",
        message: errMsg,
      });
    }
  };

  const handleSendForApproval = (approverID) => {
    const payload = {
      id: investmentID,
      name: regName,
      linkedCompanies: selectedCompanies.map((c) => c.value),
      approvalStatus: "pending approval",
      approverID: approverID,
    };
    if (onSendForApproval) onSendForApproval(payload);
    setApproverLeaseModal(false);
  };

  const effectiveCompanyOptions =
    allCompanyOptions.length > 0 ? allCompanyOptions : companyOptions;

  const filteredCompanyOptions = effectiveCompanyOptions.filter((opt) =>
    opt.label.toLowerCase().includes((dropdownOpen ? companySearch : "").toLowerCase())
  );

  const handleDropdownKeyDown = (e) => {
    if (!dropdownOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setDropdownOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredCompanyOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredCompanyOptions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredCompanyOptions.length > 0 && filteredCompanyOptions[highlightedIndex]) {
        const opt = filteredCompanyOptions[highlightedIndex];
        handleToggleCompany(opt);
      }
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
      setCompanySearch("");
    }
  };

  const allSelected =
    effectiveCompanyOptions.length > 0 &&
    selectedCompanies.length === effectiveCompanyOptions.length;

  const handleRemoveCompany = (opt) => {
    if (companiesWithInvestments.includes(String(opt.value))) {
      setWarningPopup({
        isOpen: true,
        title: "You cannot remove the company",
        message: "There are existing investments in this company, you cannot remove it",
      });
      return;
    }
    setSelectedCompanies((prev) => prev.filter((v) => v.value !== opt.value));
  };

  const handleToggleCompany = (opt) => {
    const isSelected = !!selectedCompanies.find((v) => v.value === opt.value);
    if (isSelected) {
      if (companiesWithInvestments.includes(String(opt.value))) {
        setWarningPopup({
          isOpen: true,
          title: "You cannot remove the company",
          message: "There are existing investments in this company, you cannot remove it",
        });
        return;
      }
      setSelectedCompanies((prev) => prev.filter((v) => v.value !== opt.value));
    } else {
      setSelectedCompanies((prev) => [...prev, opt]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      const hasWithInvestments = selectedCompanies.some((c) =>
        companiesWithInvestments.includes(String(c.value))
      );
      if (hasWithInvestments) {
        setWarningPopup({
          isOpen: true,
          title: "You cannot remove the company",
          message: "There are existing investments in this company, you cannot remove it",
        });
        setSelectedCompanies((prev) =>
          prev.filter((c) => companiesWithInvestments.includes(String(c.value)))
        );
        return;
      }
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies([...effectiveCompanyOptions]);
    }
  };

  const isFormValid = regName?.trim() && selectedCompanies.length > 0;

  const breadcrumbItems = [
    {
      label: "Home",
      link: `/dashboard`,
      className: `${commoncss.nav_cnt_color} clickable`,
    },
    {
      label: "Edit Register",
      link: `/dashboard`,
      className: `${commoncss.nav_cnt_color} clickable`,
    },
    { label: "Edit Investment", link: "", className: "headerLable" },
  ];

useEffect(() => {
  const fetchData = async () => {
    if (!investmentID) {
      console.error("[EditInvestmentRegister] Missing investmentID in route params", {
        registerid,
        id,
      });
      return;
    }

    if (fetchedInvestmentRef.current === investmentID) {
      return;
    }

    try {
      setLoader(true);
      fetchedInvestmentRef.current = investmentID;
      console.log("[EditInvestmentRegister] Fetching form data for investmentID:", investmentID);

      const [res, compRes, summaryRes] = await Promise.allSettled([
        getInvestmentRegisterForms(investmentID),
        GetAllCompanyData(),
        getInvestmentSummary(investmentID),
      ]);

      let compList = companyOptions;
      if (compRes.status === "fulfilled" && compRes.value?.status === 200) {
        compList = (compRes.value?.data?.getAllCompanyData || []).map((c) => ({
          label: c.company_name,
          value: c._id,
        }));
        setAllCompanyOptions(compList);
      }

      if (summaryRes.status === "fulfilled") {
        const summary = summaryRes.value?.data?.summary || [];
        const invSet = [];
        summary.forEach((row) => {
          const id1 =
            row.investorCompanyData?._id ||
            row.investorCompanyID ||
            row.latestTransaction?.investorCompanyID;
          const id2 =
            row.investeeCompanyData?._id ||
            row.investeeCompanyID ||
            row.latestTransaction?.investeeCompanyID;
          if (id1) invSet.push(String(id1));
          if (id2) invSet.push(String(id2));
        });
        setCompaniesWithInvestments(invSet);
      }

      if (res.status === "fulfilled") {
        const data =
          res.value?.data?.investmentRegister ||
          res.value?.data?.data?.investmentRegister ||
          res.value?.data?.result?.investmentRegister ||
          {};

        setRegName(data?.name || "");
        setSelectedCompanies(normalizeSelectedCompanies(data, compList));
        console.log("[EditInvestmentRegister] Prefill data loaded:", data);
      }

    } catch (error) {
      fetchedInvestmentRef.current = null;
      console.error("[EditInvestmentRegister] Error fetching data:", error);
      swal("Error", "Failed to load Investment Register data", "error");
    } finally {
      setLoader(false);
    }
  };

  fetchData();
}, [investmentID]);

  return (
    <>
      <div className="conatiner">
        {loader && <Loader2 />}

        <motion.div
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className={createRegCss.imgContainer}
        >
          <img
            src={accountant}
            alt="accountant working"
            style={{ height: "93.7vh", placeSelf: "center" }}
          />
        </motion.div>

        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className={createRegCss.formContainer}
        >
          <div className={commoncss.dFlexJustifysb} style={{ marginTop: "20px" }}>
            <Breadcrumb
              style="margin-right:auto"
              items={breadcrumbItems}
              headerActions={<></>}
            />
          </div>

          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 0, opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
          >
            <div className={createRegCss.alignForm} style={{ height: "auto", marginTop: "85px" }}>
              <div className={createRegCss.container2}>
                <div className={`${commoncss.dFlex}`} style={{ marginTop: "10px", marginBottom: "28px" }}>
                  <div
                    className={commoncss.dFlexClm}
                    style={{ alignItems: "center" }}
                  >
                    <span className={createRegCss.heading1}>
                      Edit Investment Register
                    </span>
                    <span className={createRegCss.label1} style={{ marginTop: "4px" }}>
                      Edit register name and linked companies
                    </span>
                  </div>
                </div>

                <div className={createRegCss.container3} style={{ width: "65%" }}>

                  {/* Register Name */}
                  <InputGroup
                    label="Name of Investment Register"
                    inputs={[
                      <input
                        key="reg-name"
                        name="invreg_name"
                        placeholder="Investment register"
                        type="text"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        disabled={isApprovalPending}
                      />,
                    ]}
                  />

                  {/* Linked Companies Multi-select */}
                  <div style={{ width: "100%", marginTop: "24px" }}>
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#444",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Select Linked Companies
                    </label>

                    <div
                      key="company-select"
                      ref={dropdownRef}
                      style={{ position: "relative", width: "100%" }}
                    >
                      {/* Trigger & Dropdown Menu Container */}
                      <div style={{ position: "relative", width: "100%" }}>
                        {/* Trigger */}
                        <div
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                          onClick={() => {
                            if (!isApprovalPending && !dropdownOpen) {
                              setCompanySearch("");
                              setHighlightedIndex(0);
                              setDropdownOpen(true);
                            }
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "0 12px",
                            border: "1px solid #C2E6D1",
                            borderRadius: "10px",
                            backgroundColor: isApprovalPending ? "#f0f0f0" : "#F4FAF5",
                            cursor: isApprovalPending ? "not-allowed" : "pointer",
                            minHeight: "40px",
                            fontFamily: "DM Sans, sans-serif",
                            fontSize: "13px",
                            color: "#183930",
                            width: "100%",
                            boxSizing: "border-box",
                            position: "relative"
                          }}
                        >
                          <input
                            type="text"
                            disabled={isApprovalPending}
                            value={
                              dropdownOpen
                                ? companySearch
                                : selectedCompanies.length === 0
                                ? ""
                                : selectedCompanies.map((c) => c.label).join(", ")
                            }
                            placeholder={
                              dropdownOpen
                                ? "Type to search company..."
                                : selectedCompanies.length === 0
                                ? "Select Companies"
                                : ""
                            }
                            onFocus={() => {
                              if (!isApprovalPending) {
                                setCompanySearch("");
                                setHighlightedIndex(0);
                                setDropdownOpen(true);
                              }
                            }}
                            onChange={(e) => {
                              setCompanySearch(e.target.value);
                              setHighlightedIndex(0);
                              setDropdownOpen(true);
                            }}
                            onKeyDown={handleDropdownKeyDown}
                            style={{
                              border: "none",
                              background: "transparent",
                              outline: "none",
                              color: "#183930",
                              fontSize: "13px",
                              fontFamily: "DM Sans, sans-serif",
                              width: "100%",
                              padding: 0,
                              cursor: isApprovalPending ? "not-allowed" : "pointer",
                              textOverflow: "ellipsis",
                            }}
                          />
                          {!isApprovalPending && (
                            <DownArrow
                              onClick={(e) => {
                                e.stopPropagation();
                                setDropdownOpen((prev) => !prev);
                              }}
                              style={{
                                cursor: "pointer",
                                transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                                flexShrink: 0,
                                marginLeft: "8px",
                              }}
                            />
                          )}

                          {showTooltip && !dropdownOpen && selectedCompanies.length > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "0",
                                marginTop: "8px",
                                backgroundColor: "#111111",
                                color: "#ffffff",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                fontSize: "14px",
                                width: "100%",
                                boxSizing: "border-box",
                                zIndex: 1000,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                lineHeight: "1.4"
                              }}
                            >
                              {selectedCompanies.map((c) => c.label).join(", ")}
                            </div>
                          )}
                        </div>

                        {/* Dropdown Options */}
                        {dropdownOpen && !isApprovalPending && (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 4px)",
                              left: 0,
                              right: 0,
                              backgroundColor: "#fff",
                              border: "1px solid #C2E6D1",
                              borderRadius: "10px",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                              zIndex: 999,
                              maxHeight: "280px",
                              overflowY: "auto",
                            }}
                          >
                            {/* Select All — inside dropdown, only visible when open */}
                            <div
                              onClick={handleSelectAll}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: 600,
                                fontSize: "13px",
                                background: "#ffffff",
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                borderBottom: "1px solid #C2E6D1",
                              }}
                            >
                              <input
                                type="checkbox"
                                readOnly
                                checked={allSelected}
                                style={{ accentColor: "#2d6a4f", cursor: "pointer" }}
                              />
                              Select all companies
                            </div>

                            {filteredCompanyOptions.length > 0 ? (
                              filteredCompanyOptions.map((opt, i) => {
                                const selected = !!selectedCompanies.find(
                                  (v) => v.value === opt.value
                                );
                                return (
                                  <div
                                    key={opt.value}
                                    onClick={() => handleToggleCompany(opt)}
                                    onMouseEnter={() => setHighlightedIndex(i)}
                                    style={{
                                      padding: "10px 14px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      fontSize: "13px",
                                      background: highlightedIndex === i ? "#f4faf5" : "#fff",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={selected}
                                      style={{ accentColor: "#2d6a4f", cursor: "pointer" }}
                                    />
                                    {opt.label}
                                  </div>
                                );
                              })
                            ) : (
                              <div
                                style={{
                                  padding: "10px 14px",
                                  color: "#9CA3AF",
                                  fontSize: "13px",
                                }}
                              >
                                No companies found
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Outside Select All */}
                      {!dropdownOpen && !isApprovalPending && (
                        <div
                          onClick={handleSelectAll}
                          style={{
                            padding: "6px 0 2px 0",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            fontWeight: 500,
                            fontSize: "14px",
                            background: "transparent",
                          }}
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={allSelected}
                            style={{ accentColor: "#2d6a4f", cursor: "pointer" }}
                          />
                          Select all companies
                        </div>
                      )}

                      {/* Chips */}
                      {selectedCompanies.length > 0 && (
                        <div
                          style={{
                            maxHeight: "250px",
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            background:
                              selectedCompanies.length > 3 ? "#f8f9f9" : "transparent",
                            padding: selectedCompanies.length > 3 ? "8px 10px" : "2px 0",
                            borderRadius: "10px",
                            border:
                              selectedCompanies.length > 3
                                ? "1px solid #e0e0e0"
                                : "none",
                            width: "100%",
                            boxSizing: "border-box",
                            marginTop: "4px"
                          }}
                        >
                          {selectedCompanies.map((opt) => (
                            <div
                              key={opt.value}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 10px",
                                border: "0.8px solid #C2E6D1",
                                borderRadius: "6px",
                                backgroundColor: "#fff",
                                fontFamily: "DM Sans, sans-serif",
                                fontSize: "13px",
                                color: "#183930",
                              }}
                            >
                              {opt.label}
                              {!isApprovalPending && (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveCompany(opt);
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#B3C0BD",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  >
                                    <line x1="2" y1="2" x2="12" y2="12" />
                                    <line x1="12" y1="2" x2="2" y2="12" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    style={{ width: "70%", placeSelf: "center", marginTop: "32px" }}
                  >
                    {user?.isAdmin === "Yes" && (
                      <button
                        type="submit"
                        className={`${buttoncss.primary} ${
                          !isFormValid ? buttoncss.disabledBtn : ""
                        }`}
                        style={{
                          padding: "7px",
                          width: "100%",
                          borderRadius: "12px",
                        }}
                        disabled={!isFormValid}
                        onClick={handleSave}
                      >
                        Save Changes
                      </button>
                    )}

                    {user?.responsibilities === "preparer" && (
                      <button
                        type="submit"
                        className={`${buttoncss.primary} ${
                          !isFormValid || isApprovalPending
                            ? buttoncss.disabledBtn
                            : ""
                        }`}
                        style={{
                          padding: "7px",
                          width: "100%",
                          borderRadius: "12px",
                        }}
                        onClick={() => {
                          if (isFormValid && !isApprovalPending)
                            setApproverLeaseModal(true);
                        }}
                        disabled={!isFormValid || isApprovalPending}
                      >
                        Send changes for approval
                      </button>
                    )}
                  </div>

                  {approverLeaseModal && (
                    <ApproverLeaseModal
                      setApproverLeaseModal={setApproverLeaseModal}
                      companyId={
                        selectedCompanies.length > 0
                          ? selectedCompanies[0].value
                          : null
                      }
                      setApproverId={setApproverId}
                      sendForApproval={handleSendForApproval}
                    />
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Warning Popup for Company Change / Restrictions */}
      {warningPopup.isOpen &&
        ReactDOM.createPortal(
          <AnimatePresence>
            <div
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 99999,
                backdropFilter: "blur(2px)",
              }}
              onClick={() =>
                setWarningPopup({ isOpen: false, title: "", message: "" })
              }
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  width: "480px",
                  maxWidth: "90vw",
                  padding: "36px 32px 32px 32px",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                  boxSizing: "border-box",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close X Button top right */}
                <button
                  onClick={() =>
                    setWarningPopup({ isOpen: false, title: "", message: "" })
                  }
                  style={{
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    stroke="#2D5A46"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <line x1="3" y1="3" x2="15" y2="15" />
                    <line x1="15" y1="3" x2="3" y2="15" />
                  </svg>
                </button>

                {/* Circular red/pink badge with X */}
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    backgroundColor: "#FDE8E8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#E00000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <line x1="5" y1="5" x2="19" y2="19" />
                    <line x1="19" y1="5" x2="5" y2="19" />
                  </svg>
                </div>

                {/* Title */}
                <h2
                  style={{
                    fontFamily: "inherit",
                    fontWeight: "600",
                    fontSize: "20px",
                    color: "#183930",
                    textAlign: "center",
                    margin: "0 0 10px 0",
                  }}
                >
                  {warningPopup.title || "You cannot remove the company"}
                </h2>

                {/* Subtitle */}
                <p
                  style={{
                    fontFamily: "inherit",
                    fontWeight: "400",
                    fontSize: "15px",
                    lineHeight: "1.4",
                    color: "#4D6D64",
                    textAlign: "center",
                    margin: "0 0 28px 0",
                    maxWidth: "340px",
                  }}
                >
                  {warningPopup.message ||
                    "There are existing investments in this company, you cannot remove it"}
                </p>

                {/* Red Action Button */}
                <button
                  onClick={() =>
                    setWarningPopup({ isOpen: false, title: "", message: "" })
                  }
                  style={{
                    backgroundColor: "#E00000",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "12px",
                    padding: "12px 36px",
                    fontFamily: "inherit",
                    fontWeight: "600",
                    fontSize: "15px",
                    cursor: "pointer",
                    minWidth: "160px",
                    outline: "none",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#c70000")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#E00000")
                  }
                >
                  Ok, Got it!
                </button>
              </motion.div>
            </div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default EditInvestmentRegister;